import pino from 'pino';

import type { LoggerOptions } from './types.js';

/**
 * pino applies its Error serializer to the `err` key only. The convention across
 * this repo is `logger.error({ error }, '...')`, which meant Error fields — all
 * non-enumerable — were dropped and the log recorded `"error":{}`, losing the
 * message and stack precisely when they were needed. Map both keys.
 *
 * `stdSerializers.err` returns non-Error values unchanged, so the many call
 * sites that pass a plain string or object under `error` keep their shape.
 */
const ERROR_SERIALIZERS: NonNullable<pino.LoggerOptions['serializers']> = {
  err: pino.stdSerializers.err,
  error: pino.stdSerializers.err,
};

/**
 * Credentials never reach a log line, whoever logs a headers object or an OAuth
 * result (security review 2026-09-26, L7). Paths are pino's (fast-redact):
 * case-sensitive, `*` is one level. Explicit top-level keys plus one wildcard
 * level; pino's docs note wildcards cost more, which is noise at log volume.
 */
const CREDENTIAL_KEYS = [
  'authorization',
  'cookie',
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'secret',
  'apiKey',
] as const;
const REDACT: pino.LoggerOptions['redact'] = {
  paths: [...CREDENTIAL_KEYS, ...CREDENTIAL_KEYS.map((key) => `*.${key}`)],
  censor: '[Redacted]',
};

const PRETTY_TRANSPORT: pino.TransportSingleOptions = {
  target: 'pino-pretty',
  options: {
    colorize: true,
    translateTime: 'HH:MM:ss.l',
    ignore: 'pid,hostname',
  },
};

/**
 * Creates a pino logger configured for the given environment.
 *
 * - In development: pretty-printed output (unless a browser config is provided).
 * - In production: structured JSON (or browser console, or file destination).
 *
 * @param options - Environment-specific configuration
 * @returns A configured pino.Logger instance
 */
export function createLogger(options: LoggerOptions): pino.Logger {
  const {
    processLabel,
    isDev,
    level,
    base: extraBase,
    browser,
    destination,
    mixin,
  } = options;

  const resolvedLevel = level ?? (isDev ? 'debug' : 'info');

  const baseFields: Record<string, unknown> = {
    process: processLabel,
    ...extraBase,
  };

  if (browser) {
    // Serializers are intentionally omitted: pino's browser mode only applies
    // them with `browser.serialize`, and the renderer logs through console.*
    // where DevTools already renders Error objects usefully.
    return pino({
      level: resolvedLevel,
      browser,
      base: baseFields,
      ...(mixin ? { mixin } : {}),
    });
  }

  if (isDev) {
    return pino(
      {
        level: resolvedLevel,
        transport: PRETTY_TRANSPORT,
        base: baseFields,
        serializers: ERROR_SERIALIZERS,
        redact: REDACT,
        ...(mixin ? { mixin } : {}),
      },
    );
  }

  const loggerOptions: pino.LoggerOptions = {
    level: resolvedLevel,
    base: baseFields,
    serializers: ERROR_SERIALIZERS,
    redact: REDACT,
    ...(mixin ? { mixin } : {}),
  };

  if (destination) {
    return pino(loggerOptions, destination);
  }

  return pino(loggerOptions);
}
